import { ArrowUp } from "lucide-react";
import { Link } from "react-router-dom";
import { cities } from "@/data/cities";
import { services } from "@/data/services";
import { localize } from "@/utils/paths";

export const Footer = ({ t, scrollTo, lang }) => {
  const go = (e, hash) => {
    e.preventDefault();
    scrollTo(hash);
  };

  return (
    <footer className="bg-ink text-cream" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10 md:gap-8 pb-20 border-b border-cream/15">
          <div className="col-span-2 md:col-span-4">
            <p className="font-heading text-3xl font-medium">
              Swiss <span className="italic text-taupe">PA Services</span>
            </p>
            <p className="mt-4 text-cream/60 text-sm font-light tracking-wide" data-testid="footer-tagline">
              {t.footer.tagline}
            </p>
            <p className="mt-6 text-cream/40 text-xs leading-relaxed max-w-sm" data-testid="footer-seo-text">
              {t.footer.seo}
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-cream/50 mb-6">{t.footer.servicesTitle}</p>
            <ul className="space-y-3 text-sm">
              {services.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={localize(`/leistungen/${s.slug}`, lang)}
                    data-testid={`footer-service-${s.slug}`}
                    className="text-cream/75 hover:text-cream transition-colors duration-300"
                  >
                    {s[lang].short}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to={lang === "en" ? "/en/journal" : "/ratgeber"}
                  data-testid="footer-journal-link"
                  className="text-cream/75 hover:text-cream transition-colors duration-300"
                >
                  {t.footer.journal}
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-cream/50 mb-6">{t.footer.locationsTitle}</p>
            <ul className="space-y-3 text-sm">
              {cities.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={localize(`/standorte/${c.slug}`, lang)}
                    data-testid={`footer-location-${c.slug}`}
                    className="text-cream/75 hover:text-cream transition-colors duration-300"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-cream/50 mb-6">{t.footer.contactTitle}</p>
            <address className="not-italic space-y-3 text-sm text-cream/75" data-testid="footer-contact-info">
              <p>Swiss PA Services</p>
              <p>Baarerstrasse 43, 6300 Zug</p>
              <p>
                <a href="mailto:info@swiss-pa-services.ch" data-testid="footer-email-link" className="hover:text-cream transition-colors duration-300">
                  info@swiss-pa-services.ch
                </a>
              </p>
              <p>
                <a href="tel:+41446880380" data-testid="footer-phone-link" className="hover:text-cream transition-colors duration-300">
                  +41 44 688 03 80
                </a>
              </p>
            </address>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 py-8 text-xs text-cream/45">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} Swiss PA Services. {t.footer.rights}</p>
          <div className="flex items-center gap-6">
            <Link to={localize("/impressum", lang)} data-testid="footer-imprint-link" className="hover:text-cream transition-colors duration-300">
              {t.footer.imprint}
            </Link>
            <Link to={localize("/datenschutz", lang)} data-testid="footer-privacy-link" className="hover:text-cream transition-colors duration-300">
              {t.footer.privacy}
            </Link>
            <button
              onClick={() => window.__lenis && window.__lenis.scrollTo(0, { duration: 1.6 })}
              data-testid="footer-back-to-top"
              aria-label="Back to top"
              className="p-2 border border-cream/20 hover:bg-cream/10 transition-colors duration-300"
            >
              <ArrowUp size={14} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div className="overflow-hidden pb-4 select-none" aria-hidden="true">
          <p className="font-heading font-light leading-[0.8] tracking-tight text-[20vw] md:text-[16vw] text-cream/10 whitespace-nowrap text-center" data-testid="footer-giant-brand">
            SWISS PA
          </p>
        </div>
      </div>
    </footer>
  );
};
